import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import adminApi from "../../api/adminApi";
import { errorText } from "../../utils/adminUtils";
import {
  campusOptions,
  filterCandidates,
  stayLabel,
  staysByAdult,
  visibleStays,
} from "../../utils/supervisionStays";

/**
 * Who supervises whom. A flat list of (supervisor, supervisee) staff links —
 * no chains, no dates. It decides whose overtime and hours appear in a
 * supervisor's review screen.
 *
 * The grant and the list are independent, and neither empty state is an
 * error: the SUPERVISOR grant opens the screen, this list fills it. A grant
 * with no rows is an empty screen; rows without the grant show nobody
 * anything. Both are called out below so an admin can see which they have.
 */
export default function SupervisionEditor({ users = [], setBanner }) {
  const [staff, setStaff] = useState([]);
  const [links, setLinks] = useState([]);
  const [stayRows, setStayRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [supervisorId, setSupervisorId] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState("");
  const [campus, setCampus] = useState("");
  const [includePast, setIncludePast] = useState(false);
  const [saving, setSaving] = useState(false);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [staffRows, linkRows, stays] = await Promise.all([
        adminApi.getStaff(),
        adminApi.getSupervision(),
        // Assignments for every year in one call; the screen shows current and
        // upcoming unless past ones are asked for.
        adminApi.getStaffStays().catch(() => []),
      ]);
      // /api/staff answers 204 when there are none, which the wrapper gives as null.
      setStaff(Array.isArray(staffRows) ? staffRows : []);
      setLinks(Array.isArray(linkRows) ? linkRows : []);
      setStayRows(Array.isArray(stays) ? stays : []);
    } catch (error) {
      setLoadError(errorText(error, "Failed to load supervision"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const staffById = useMemo(() => {
    const map = new Map();
    staff.forEach((s) => map.set(Number(s.staffid), s));
    return map;
  }, [staff]);

  // The grant lives on the user account, the list on the staff row; email is
  // what ties them together.
  const grantedEmails = useMemo(
    () =>
      new Set(
        users
          .filter((u) => (u.grants || []).includes("SUPERVISOR") || u.role === "ADMIN")
          .map((u) => String(u.email || "").toLowerCase())
      ),
    [users]
  );

  const holdsGrant = useCallback(
    (staffRow) => grantedEmails.has(String(staffRow?.username || "").toLowerCase()),
    [grantedEmails]
  );

  const superviseeCounts = useMemo(() => {
    const counts = new Map();
    links.forEach((l) => {
      const key = Number(l.supervisorStaffId);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [links]);

  // Supervisors first — anyone who holds the grant or already has a list —
  // then the rest of the staff, so a new list can be started for anyone.
  const supervisorOptions = useMemo(() => {
    const rank = (s) => {
      const isSupervisor = holdsGrant(s) || superviseeCounts.has(Number(s.staffid));
      return isSupervisor ? 0 : 1;
    };
    return staff
      .slice()
      .sort(
        (a, b) =>
          rank(a) - rank(b) || String(a.name || "").localeCompare(String(b.name || ""))
      );
  }, [staff, holdsGrant, superviseeCounts]);

  const selectedSupervisor = supervisorId ? staffById.get(Number(supervisorId)) : null;

  // Loading a supervisor replaces the working set with what is stored.
  const chooseSupervisor = (value) => {
    setSupervisorId(value);
    setFilter("");
    const current = links
      .filter((l) => String(l.supervisorStaffId) === String(value))
      .map((l) => Number(l.staffId));
    setSelected(new Set(current));
  };

  const storedIds = useMemo(
    () =>
      new Set(
        links
          .filter((l) => String(l.supervisorStaffId) === String(supervisorId))
          .map((l) => Number(l.staffId))
      ),
    [links, supervisorId]
  );

  const dirty = useMemo(() => {
    if (!supervisorId) return false;
    if (storedIds.size !== selected.size) return true;
    for (const id of selected) if (!storedIds.has(id)) return true;
    return false;
  }, [supervisorId, storedIds, selected]);

  const staysByAdultId = useMemo(() => staysByAdult(stayRows), [stayRows]);
  const campuses = useMemo(() => campusOptions(stayRows), [stayRows]);

  const candidates = useMemo(
    () =>
      filterCandidates(
        staff.filter((s) => Number(s.staffid) !== Number(supervisorId)),
        { staysByAdultId, today, includePast, campus, text: filter, selected }
      ),
    [staff, supervisorId, staysByAdultId, today, includePast, campus, filter, selected]
  );

  const toggle = (staffId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = Number(staffId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!supervisorId) return;
    setSaving(true);
    try {
      await adminApi.setSupervision(Number(supervisorId), [...selected]);
      await load();
      setBanner?.({
        type: "success",
        text: `${selectedSupervisor?.name || "Supervisor"} supervises ${selected.size} ${
          selected.size === 1 ? "person" : "people"
        }`,
      });
    } catch (error) {
      setBanner?.({ type: "error", text: errorText(error, "Failed to save supervision") });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  return (
    // .App centres text app-wide; names, dates and prose all read left here.
    <Stack spacing={2} sx={{ textAlign: "left" }}>
      <Typography variant="body2" sx={{ opacity: 0.8 }}>
        The list decides whose overtime and hours a supervisor sees; the SUPERVISOR
        grant (Users tab) decides whether they see the screen at all. Admins see
        everyone regardless.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <Select
          size="small"
          displayEmpty
          value={supervisorId}
          onChange={(e) => chooseSupervisor(e.target.value)}
          sx={{ minWidth: 320 }}
        >
          <MenuItem value="">
            <em>Choose a supervisor…</em>
          </MenuItem>
          {supervisorOptions.map((s) => {
            const count = superviseeCounts.get(Number(s.staffid)) || 0;
            return (
              <MenuItem key={s.staffid} value={String(s.staffid)}>
                {s.name}
                {holdsGrant(s) ? "" : " — no grant"}
                {count ? ` (${count})` : ""}
              </MenuItem>
            );
          })}
        </Select>

        {selectedSupervisor && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
            <Chip
              size="small"
              label={`${selected.size} supervised`}
              color={selected.size ? "primary" : "default"}
            />
            {!holdsGrant(selectedSupervisor) && (
              <Chip size="small" color="warning" label="No SUPERVISOR grant" />
            )}
          </Stack>
        )}
      </Stack>

      {!selectedSupervisor ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Pick a supervisor to see and edit their list.
        </Typography>
      ) : (
        <>
          {!holdsGrant(selectedSupervisor) && (
            <Alert severity="warning">
              {selectedSupervisor.name} has no SUPERVISOR grant, so this list shows them
              nothing. Give them the grant on the Users tab.
            </Alert>
          )}

          {holdsGrant(selectedSupervisor) && selected.size === 0 && (
            <Alert severity="info">
              {selectedSupervisor.name} holds the grant but supervises nobody — their
              review screen will be empty until someone is ticked below.
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Filter staff"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 240 }}
            />

            <Select
              size="small"
              displayEmpty
              value={campus}
              onChange={(e) => setCampus(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">
                <em>Any campus</em>
              </MenuItem>
              {campuses.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>

            <FormControlLabel
              label="Include past assignments"
              control={
                <Checkbox
                  size="small"
                  checked={includePast}
                  onChange={(e) => setIncludePast(e.target.checked)}
                />
              }
            />
          </Stack>

          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 460, overflow: "auto" }}>
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} columnGap={2}>
              {candidates.map((s) => {
                const stays = visibleStays(staysByAdultId.get(Number(s.adultid)) || [], {
                  today,
                  includePast,
                  campus,
                });
                return (
                  <FormControlLabel
                    key={s.staffid}
                    sx={{
                      alignItems: "flex-start",
                      mr: 0,
                      py: 0.25,
                      "& .MuiCheckbox-root": { pt: 0.25 },
                    }}
                    label={
                      <Box>
                        <Typography variant="body2">{s.name}</Typography>
                        {stays.length === 0 ? (
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>
                            {includePast ? "No assignments" : "No current assignment"}
                          </Typography>
                        ) : (
                          stays.map((stay) => (
                            <Typography
                              key={stay.id}
                              variant="caption"
                              display="block"
                              sx={{ opacity: 0.7 }}
                            >
                              {stayLabel(stay, today)}
                            </Typography>
                          ))
                        )}
                      </Box>
                    }
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.has(Number(s.staffid))}
                        onChange={() => toggle(s.staffid)}
                      />
                    }
                  />
                );
              })}
              {candidates.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.7, p: 1 }}>
                  No staff match those filters.
                </Typography>
              )}
            </Box>
          </Paper>

          <Divider />

          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={!dirty || saving} onClick={handleSave}>
              {saving ? "Saving…" : "Save list"}
            </Button>
            <Button
              disabled={!dirty || saving}
              onClick={() => chooseSupervisor(supervisorId)}
            >
              Discard changes
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
